import { t } from '@lingui/macro';
import { msg } from '@lingui/core/macro';
import { useLingui } from '@lingui/react';
import React, { Suspense } from 'react';
import { useQuery } from '@tanstack/react-query';
import { Link, Outlet, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@shared/hooks/useAuth';
import { useUnreadCounts } from '@shared/hooks/message/useUnreadCounts';
import { useIsMobile } from '@shared/hooks/use-mobile';
import { fetchUnreadCounts } from '@shared/api/messaging';
import {
  useApproveDoctorAccountControlRequest,
  useDenyDoctorAccountControlRequest,
  useDoctorAccountControlDoctorInbox,
  useDoctorAccountControlDoctorSummary,
  useEndDoctorAccountControl,
  useRevokeDoctorAccountControl,
} from '@shared/hooks/api/useDoctorAccountControl';
import { getUnreadPatientsCount } from '@features/doctor/pages/patients/services/patientsService';
import { buildMessagingContextKey } from '@shared/lib/messagingScope';
import { getNavItemsForRole, getNavItemByPath } from '@shared/lib/navigation';
import { queryKeys } from '@shared/lib/react-query';
import { clearDoctorControlStorage, useDoctorControlStore } from '@shared/stores/doctor-control-store';
import { useDoctorWorkspace } from '@shared/hooks/useDoctorWorkspace';
import { isDoctorWorkspacePath, toDoctorWorkspacePath } from '@shared/lib/doctor-workspace';
import { localizePath, useLocale } from '@shared/i18n';
import { Building, ShieldCheck } from 'lucide-react';
import {
  SidebarProvider,
  SidebarInset
} from '@shared/ui/sidebar';
import { AppSidebar } from '@shared/layout/AppSidebar';
import { AppHeader } from '@shared/layout/AppHeader';
import { PatientMobileHeader } from '@shared/layout/PatientMobileHeader';
import { DoctorMobileHeader } from '@shared/layout/DoctorMobileHeader';
import { MobileBottomNav } from '@shared/layout/MobileBottomNav';

import { ErrorBoundary, LoadingSkeleton } from '@shared/components';
import { Alert, AlertDescription, AlertTitle } from '@shared/ui/alert';
import { Button } from '@shared/ui/button';
import { Dialog, DialogContent, DialogDescription, DialogTitle } from '@shared/ui/dialog';
import { Avatar, AvatarFallback } from '@shared/ui/avatar';
import { toast } from 'sonner';

interface AppShellProps {
  children?: React.ReactNode;
}

const MESSAGING_ENABLED = import.meta.env.VITE_FEATURE_MESSAGING !== 'false';

export const AppShell: React.FC<AppShellProps> = ({ children }) => {
  const { user, authContext, isLoading } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const { i18n } = useLingui();
  const { locale } = useLocale();
  const isMobile = useIsMobile();
  const activeDoctorControlRequest = useDoctorControlStore((state) => state.activeRequest);
  const {
    effectivePortalRole,
    isEffectiveOrganizationOwner,
    isDelegatedDoctorWorkspace,
    normalizedPathname,
    resolveDoctorPath,
    returnPath,
    workspaceHomePath,
  } = useDoctorWorkspace();
  const clearDoctorControl = useDoctorControlStore((state) => state.clearControl);
  const [doctorApprovalDialogOpen, setDoctorApprovalDialogOpen] = React.useState(false);
  const lastPromptedRequestIdRef = React.useRef<string | null>(null);

  // Get current page info for breadcrumbs and title
  const baseNavItems = getNavItemsForRole(effectivePortalRole);
  const navItems = React.useMemo(() => {
    if (!user) return baseNavItems;
    let items = [...baseNavItems];

    const isOwner = effectivePortalRole === 'doctor' && isEffectiveOrganizationOwner;
    if (isOwner && !items.find((item) => item.key === 'manage-organization')) {
      items = [
        ...items,
        {
          key: 'manage-organization',
          label: msg({
            id: 'shared.layout.AppShell.label-manage-organization',
            message: 'Manage Organization',
          }),
          path: '/organization/manage',
          icon: Building,
          roles: ['doctor'],
          description: msg({
            id: 'shared.layout.AppShell.description-organization-configuration-and-members',
            message: 'Organization configuration and members',
          }),
        },
      ];
    }

    return isDelegatedDoctorWorkspace
      ? items.map((item) => ({
          ...item,
          path: resolveDoctorPath(item.path),
        }))
      : items;
  }, [
    baseNavItems,
    effectivePortalRole,
    isDelegatedDoctorWorkspace,
    isEffectiveOrganizationOwner,
    resolveDoctorPath,
    user,
  ]);

  const { unreadCounts, loading: unreadLoading } = useUnreadCounts({
    userId: user?.id,
    enabled: Boolean(user && MESSAGING_ENABLED && !isDelegatedDoctorWorkspace),
  });

  const delegatedMessagingContextKey = React.useMemo(
    () =>
      buildMessagingContextKey({
        authUserId: authContext?.authUserId ?? user?.id,
        activeOrganizationId: authContext?.activeOrganizationId ?? user?.active_organization_id,
        activeRoleTemplateCode: authContext?.activeRoleTemplateCode ?? user?.active_role_template_code,
        isDelegatedDoctorWorkspace,
        doctorControlRequestId: activeDoctorControlRequest?.id,
      }),
    [
      activeDoctorControlRequest?.id,
      authContext?.activeOrganizationId,
      authContext?.activeRoleTemplateCode,
      authContext?.authUserId,
      isDelegatedDoctorWorkspace,
      user?.active_organization_id,
      user?.active_role_template_code,
      user?.id,
    ],
  );

  const delegatedUnreadCountsQuery = useQuery({
    queryKey: [...queryKeys.messaging.all, 'unread-count', 'doctor', delegatedMessagingContextKey] as const,
    queryFn: () =>
      fetchUnreadCounts('doctor', {
        timeoutProfile: 'messaging',
        requestLabel: 'doctor-control:messages-unread-count',
      }),
    enabled: Boolean(isDelegatedDoctorWorkspace && MESSAGING_ENABLED),
    staleTime: 50_000,
    refetchInterval: 50_000,
    refetchIntervalInBackground: false,
  });

  const doctorPatientsUnreadQuery = useQuery({
    queryKey: queryKeys.doctor.patientsUnreadCount(),
    queryFn: getUnreadPatientsCount,
    enabled: effectivePortalRole === 'doctor',
    staleTime: 30_000,
  });

  const doctorControlInboxQuery = useDoctorAccountControlDoctorInbox(user?.role === 'doctor');
  const adminDoctorControlSummaryQuery = useDoctorAccountControlDoctorSummary(
    activeDoctorControlRequest?.doctorId,
    (user?.role === 'admin' || user?.role === 'super_admin') &&
      activeDoctorControlRequest?.status === 'active' &&
      Boolean(activeDoctorControlRequest?.doctorId),
  );
  const approveDoctorControlMutation = useApproveDoctorAccountControlRequest();
  const denyDoctorControlMutation = useDenyDoctorAccountControlRequest();
  const revokeDoctorControlMutation = useRevokeDoctorAccountControl();
  const endDoctorControlMutation = useEndDoctorAccountControl();

  const pendingDoctorControlRequest = doctorControlInboxQuery.data?.pendingRequest ?? null;
  const activeDoctorControlSession = doctorControlInboxQuery.data?.activeSession ?? null;
  const isAdminInActiveControlMode =
    (user?.role === 'admin' || user?.role === 'super_admin') &&
    activeDoctorControlRequest?.status === 'active';
  const shouldThrottleControlModeNetwork =
    isDelegatedDoctorWorkspace ||
    (user?.role === 'doctor' && Boolean(activeDoctorControlSession));

  React.useEffect(() => {
    if (user?.role !== 'admin' && user?.role !== 'super_admin') {
      clearDoctorControl();
    }
  }, [clearDoctorControl, user?.role]);

  React.useEffect(() => {
    if (user?.role !== 'doctor') {
      return;
    }

    if (!pendingDoctorControlRequest?.id) {
      return;
    }

    if (lastPromptedRequestIdRef.current === pendingDoctorControlRequest.id) {
      return;
    }

    lastPromptedRequestIdRef.current = pendingDoctorControlRequest.id;
    setDoctorApprovalDialogOpen(true);
  }, [pendingDoctorControlRequest?.id, user?.role]);

  React.useEffect(() => {
    if (activeDoctorControlRequest?.status !== 'active') {
      return;
    }

    if (user?.role !== 'admin' && user?.role !== 'super_admin') {
      clearDoctorControlStorage();
    }
  }, [activeDoctorControlRequest?.status, user?.role]);

  React.useEffect(() => {
    if (
      !isAdminInActiveControlMode ||
      !activeDoctorControlRequest?.id ||
      !adminDoctorControlSummaryQuery.data
    ) {
      return;
    }

    if (adminDoctorControlSummaryQuery.data.activeSession?.id !== activeDoctorControlRequest.id) {
      clearDoctorControlStorage();
    }
  }, [
    activeDoctorControlRequest?.id,
    adminDoctorControlSummaryQuery.data,
    isAdminInActiveControlMode,
  ]);

  React.useEffect(() => {
    if (!isAdminInActiveControlMode || isDelegatedDoctorWorkspace) {
      return;
    }

    const currentPath = `${location.pathname}${location.search}${location.hash}`;
    if (!isDoctorWorkspacePath(currentPath)) {
      return;
    }

    const delegatedPath = toDoctorWorkspacePath(currentPath);
    if (delegatedPath === currentPath) {
      return;
    }

    navigate(delegatedPath, { replace: true });
  }, [
    isAdminInActiveControlMode,
    isDelegatedDoctorWorkspace,
    location.hash,
    location.pathname,
    location.search,
    navigate,
  ]);

  const handleApproveDoctorControl = React.useCallback(async () => {
    if (!pendingDoctorControlRequest) return;

    try {
      await approveDoctorControlMutation.mutateAsync(pendingDoctorControlRequest.id);
      toast.success(i18n._(t({
        id: 'shared.layout.AppShell.doctor-control-approved',
        message: 'Access approved',
      })));
      setDoctorApprovalDialogOpen(false);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : i18n._(
              t({
                id: 'shared.layout.AppShell.unable-to-approve-request',
                message: 'Unable to approve request',
              }),
            ),
      );
    }
  }, [approveDoctorControlMutation, i18n, pendingDoctorControlRequest]);

  const handleDenyDoctorControl = React.useCallback(async () => {
    if (!pendingDoctorControlRequest) return;

    try {
      await denyDoctorControlMutation.mutateAsync(pendingDoctorControlRequest.id);
      toast.success(i18n._(t({
        id: 'shared.layout.AppShell.doctor-control-denied',
        message: 'Access denied',
      })));
      setDoctorApprovalDialogOpen(false);
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : i18n._(
              t({
                id: 'shared.layout.AppShell.unable-to-deny-request',
                message: 'Unable to deny request',
              }),
            ),
      );
    }
  }, [denyDoctorControlMutation, i18n, pendingDoctorControlRequest]);

  const handleRevokeDoctorControl = React.useCallback(async () => {
    if (!activeDoctorControlSession) return;

    try {
      await revokeDoctorControlMutation.mutateAsync(activeDoctorControlSession.id);
      toast.success(i18n._(t({
        id: 'shared.layout.AppShell.doctor-control-revoked',
        message: 'Access revoked',
      })));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : i18n._(
              t({
                id: 'shared.layout.AppShell.unable-to-revoke-access',
                message: 'Unable to revoke access',
              }),
            ),
      );
    }
  }, [activeDoctorControlSession, i18n, revokeDoctorControlMutation]);

  const handleEndDoctorControl = React.useCallback(async () => {
    if (!activeDoctorControlRequest) return;

    try {
      await endDoctorControlMutation.mutateAsync(activeDoctorControlRequest.id);
      clearDoctorControlStorage();
      toast.success(i18n._(t({
        id: 'shared.layout.AppShell.doctor-control-ended',
        message: 'Control mode ended',
      })));
    } catch (error) {
      toast.error(
        error instanceof Error
          ? error.message
          : i18n._(
              t({
                id: 'shared.layout.AppShell.unable-to-end-control-mode',
                message: 'Unable to end control mode',
              }),
            ),
      );
    }
  }, [activeDoctorControlRequest, endDoctorControlMutation, i18n]);

  const workspaceTogglePath = isDelegatedDoctorWorkspace ? returnPath : workspaceHomePath;
  const localizedWorkspaceTogglePath = localizePath(workspaceTogglePath, locale);
  const workspaceToggleLabel = isDelegatedDoctorWorkspace
    ? i18n._(t({
        id: 'shared.layout.AppShell.return-to-admin',
        message: 'Return to admin',
      }))
    : i18n._(t({
        id: 'shared.layout.AppShell.return-to-doctor',
        message: 'Return to doctor',
      }));
  const requesterRole = String(activeDoctorControlSession?.requester.role ?? '');
  const requesterRoleLabel =
    requesterRole === 'super_admin' || requesterRole === 'superadmin'
    ? i18n._(t({
        id: 'shared.layout.AppShell.role-super-admin',
        message: 'Super Admin',
      }))
    : i18n._(t({
        id: 'shared.layout.AppShell.role-admin',
        message: 'Admin',
      }));

  const shellBanner = (
    <>
      {user?.role === 'doctor' && pendingDoctorControlRequest && (
        <Alert className="border-blue-300 bg-blue-50 text-blue-950">
          <ShieldCheck className="h-4 w-4" />
          <AlertTitle>
            {i18n._(t({
              id: 'shared.layout.AppShell.account-control-request-pending',
              message: 'Account control request pending',
            }))}
          </AlertTitle>
          <AlertDescription className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <span>
              {i18n._(t({
                id: 'shared.layout.AppShell.requester-needs-your-approval',
                message: '{requesterName} wants temporary access to act on your behalf.',
              }, {
                requesterName: pendingDoctorControlRequest.requester.fullName,
              }))}
            </span>
            <Button size="sm" onClick={() => setDoctorApprovalDialogOpen(true)}>
              {i18n._(t({
                id: 'shared.layout.AppShell.review-request',
                message: 'Review request',
              }))}
            </Button>
          </AlertDescription>
        </Alert>
      )}

    </>
  );

  const controlModeTopBar = isAdminInActiveControlMode
    ? (
      <div className="sticky top-0 z-50 shrink-0 border-b border-slate-800 bg-slate-950 text-slate-100">
        <div className="flex min-h-12 items-center justify-between gap-2 px-3 py-1.5 sm:px-4 lg:px-6">
          <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
            <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-400 text-slate-950 shadow-sm sm:h-auto sm:w-auto sm:rounded-md sm:px-2.5 sm:py-1 sm:text-xs sm:font-semibold sm:uppercase sm:tracking-[0.1em]">
              <ShieldCheck className="h-4 w-4 sm:mr-1.5 sm:h-3.5 sm:w-3.5" />
              <span className="hidden sm:inline">
                {i18n._(t({
                  id: 'shared.layout.AppShell.delegated-control-mode',
                  message: 'Delegated control',
                }))}
              </span>
            </div>
            <div className="min-w-0 flex-1">
              <p className="truncate text-xs font-semibold leading-tight sm:text-sm text-slate-100">
                {i18n._(t({
                  id: 'shared.layout.AppShell.you-are-managing-account',
                  message: 'Managing: {doctorIdentity}',
                }, {
                  doctorIdentity:
                    activeDoctorControlRequest.doctor.fullName || activeDoctorControlRequest.doctor.email,
                }))}
              </p>
              <p className="hidden truncate text-xs text-slate-400 lg:block">
                {i18n._(t({
                  id: 'shared.layout.AppShell.admin-session-remains-active',
                  message: 'Your {roleLabel} session remains active.',
                }, {
                  roleLabel:
                    user?.role === 'super_admin'
                      ? i18n._(
                          t({
                            id: 'shared.layout.AppShell.role-super-admin-lowercase',
                            message: 'super admin',
                          }),
                        )
                      : i18n._(
                          t({
                            id: 'shared.layout.AppShell.role-admin-lowercase',
                            message: 'admin',
                          }),
                        ),
                }))}
              </p>
            </div>
          </div>

          <div className="flex shrink-0 items-center gap-1.5 sm:gap-2">
            <Button asChild size="sm" variant="ghost" className="h-8 border border-slate-700 bg-slate-900 px-2.5 text-xs text-slate-100 hover:bg-slate-800 hover:text-white sm:px-3">
              <Link to={localizedWorkspaceTogglePath}>
                <span className="hidden sm:inline">{workspaceToggleLabel}</span>
                <span className="sm:hidden">
                  {i18n._(t({
                    id: 'shared.layout.AppShell.back',
                    message: 'Back',
                  }))}
                </span>
              </Link>
            </Button>
            <Button
              size="sm"
              className="h-8 bg-brand-blue px-2.5 text-xs text-white hover:bg-brand-blue/90 sm:px-3"
              onClick={() => void handleEndDoctorControl()}
              disabled={endDoctorControlMutation.isPending}
            >
              {i18n._(t({
                id: 'shared.layout.AppShell.exit-control-mode',
                message: 'Exit',
              }))}
            </Button>
          </div>
        </div>
      </div>
    )
    : user?.role === 'doctor' && activeDoctorControlSession
      ? (
        <div className="sticky top-0 z-50 shrink-0 border-b border-slate-800 bg-slate-950 text-slate-100">
          <div className="flex min-h-12 items-center justify-between gap-2 px-3 py-1.5 sm:px-4 lg:px-6">
            <div className="flex min-w-0 flex-1 items-center gap-2 sm:gap-3">
              <div className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full bg-amber-400 text-slate-950 shadow-sm sm:h-auto sm:w-auto sm:rounded-md sm:px-2.5 sm:py-1 sm:text-xs sm:font-semibold sm:uppercase sm:tracking-[0.1em]">
                <ShieldCheck className="h-4 w-4 sm:mr-1.5 sm:h-3.5 sm:w-3.5" />
                <span className="hidden sm:inline">
                  {i18n._(t({
                    id: 'shared.layout.AppShell.account-is-controlled',
                    message: 'Controlled',
                  }))}
                </span>
              </div>
              <div className="min-w-0 flex-1">
                <p className="truncate text-xs font-semibold leading-tight sm:text-sm text-slate-100">
                  {i18n._(t({
                    id: 'shared.layout.AppShell.account-controlled-by-requester',
                    message: '{requesterName} is controlling your account',
                  }, {
                    requesterName: activeDoctorControlSession.requester.fullName,
                  }))}
                </p>
                <div className="hidden items-center gap-1.5 lg:flex">
                  <span className="rounded border border-slate-700 bg-slate-900 px-1.5 py-0.5 text-[10px] font-medium uppercase tracking-wider text-slate-200">
                    {requesterRoleLabel}
                  </span>
                  <span className="truncate text-xs text-slate-400">
                    {i18n._(t({
                      id: 'shared.layout.AppShell.revoke-any-time',
                      message: 'You can stop this session anytime.',
                    }))}
                  </span>
                </div>
              </div>
            </div>

            <div className="flex shrink-0 items-center">
              <Button
                size="sm"
                className="h-8 bg-brand-blue px-2.5 text-xs text-white hover:bg-brand-blue/90 sm:px-3"
                onClick={() => void handleRevokeDoctorControl()}
                disabled={revokeDoctorControlMutation.isPending}
              >
                {i18n._(t({
                  id: 'shared.layout.AppShell.stop-control',
                  message: 'Stop',
                }))}
              </Button>
            </div>
          </div>
        </div>
      )
      : null;

  const doctorApprovalDialog = pendingDoctorControlRequest ? (
    <Dialog open={doctorApprovalDialogOpen} onOpenChange={setDoctorApprovalDialogOpen}>
      <DialogContent className="max-w-[400px] overflow-hidden rounded-2xl p-0 sm:rounded-3xl">
        <div className="flex flex-col items-center justify-center bg-blue-50/50 px-6 py-8 text-center dark:bg-blue-950/20">
          <div className="mb-4 flex h-14 w-14 items-center justify-center rounded-full bg-blue-100 dark:bg-blue-900/50">
            <ShieldCheck className="h-7 w-7 text-blue-600 dark:text-blue-400" />
          </div>
          <DialogTitle className="text-xl font-bold tracking-tight">
            {i18n._(t({
              id: 'shared.layout.AppShell.review-account-control-request',
              message: 'Account Control Request',
            }))}
          </DialogTitle>
          <DialogDescription className="mt-2 text-sm text-muted-foreground">
            {i18n._(t({
              id: 'shared.layout.AppShell.approval-keeps-admin-in-own-session',
              message: 'Grant temporary access to act on your behalf. You can revoke it anytime.',
            }))}
          </DialogDescription>
        </div>
        <div className="px-6 py-6">
          <div className="mb-5 flex items-center gap-3 rounded-xl border border-border/40 bg-muted/10 p-3 shadow-sm">
            <Avatar className="h-10 w-10 border border-border/50">
              <AvatarFallback className="bg-primary/5 text-primary text-xs font-medium">
                {`${pendingDoctorControlRequest.requester.firstName.charAt(0)}${pendingDoctorControlRequest.requester.lastName.charAt(0)}`.trim() ||
                  i18n._(
                    t({
                      id: 'shared.layout.AppShell.initials-fallback-jm',
                      message: 'JM',
                    }),
                  )}
              </AvatarFallback>
            </Avatar>
            <div className="flex flex-col">
              <span className="text-sm font-semibold">{pendingDoctorControlRequest.requester.fullName}</span>
              <span className="text-xs font-medium text-muted-foreground">
                {pendingDoctorControlRequest.requester.role === 'super_admin'
                  ? i18n._(
                      t({
                        id: 'shared.layout.AppShell.requester-role-super-admin',
                        message: 'Super Admin',
                      }),
                    )
                  : i18n._(
                      t({
                        id: 'shared.layout.AppShell.requester-role-admin',
                        message: 'Admin',
                      }),
                    )}
              </span>
            </div>
          </div>
          <div className="mb-2">
            <h4 className="text-xs font-semibold uppercase tracking-wider text-muted-foreground">
              {i18n._(t({
                id: 'shared.layout.AppShell.request-reason',
                message: 'Reason for access',
              }))}
            </h4>
          </div>
          <div className="rounded-xl bg-muted/30 p-4 text-sm leading-relaxed text-foreground shadow-inner border border-border/20 max-h-[120px] overflow-y-auto">
            {pendingDoctorControlRequest.reason}
          </div>
        </div>
        <div className="flex flex-col gap-2 bg-muted/20 px-6 py-4 sm:flex-row sm:justify-end">
          <Button
            variant="outline"
            className="w-full sm:w-auto"
            onClick={() => void handleDenyDoctorControl()}
            disabled={denyDoctorControlMutation.isPending || approveDoctorControlMutation.isPending}
          >
            {i18n._(t({
              id: 'shared.layout.AppShell.deny-access',
              message: 'Deny',
            }))}
          </Button>
          <Button
            className="w-full sm:w-auto bg-blue-600 hover:bg-blue-700 text-white"
            onClick={() => void handleApproveDoctorControl()}
            disabled={denyDoctorControlMutation.isPending || approveDoctorControlMutation.isPending}
          >
            {i18n._(t({
              id: 'shared.layout.AppShell.approve-access',
              message: 'Approve Access',
            }))}
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  ) : null;

  const displayNavItems = React.useMemo(() => {
    const filtered = MESSAGING_ENABLED ? navItems : navItems.filter((item) => item.key !== 'messages');
    const doctorPatientsUnread = doctorPatientsUnreadQuery.data ?? 0;
    const resolvedUnreadMessages = isDelegatedDoctorWorkspace
      ? delegatedUnreadCountsQuery.data?.unreadMessages ?? 0
      : unreadCounts.unreadMessages;
    const resolvedUnreadLoading = isDelegatedDoctorWorkspace
      ? delegatedUnreadCountsQuery.isLoading
      : unreadLoading;
    return filtered.map((item) => {
      if (item.key === 'messages') {
        return { ...item, badge: resolvedUnreadLoading ? item.badge : resolvedUnreadMessages };
      }
      if (item.key === 'patients' && effectivePortalRole === 'doctor') {
        return { ...item, badge: doctorPatientsUnread };
      }
      return item;
    });
  }, [
    delegatedUnreadCountsQuery.data?.unreadMessages,
    delegatedUnreadCountsQuery.isLoading,
    doctorPatientsUnreadQuery.data,
    effectivePortalRole,
    isDelegatedDoctorWorkspace,
    navItems,
    unreadCounts.unreadMessages,
    unreadLoading,
  ]);

  const currentNavItem = React.useMemo(() => {
    const explicit = getNavItemByPath(normalizedPathname, effectivePortalRole);
    const sourceNav = displayNavItems;
    return explicit ?? sourceNav.find((item) => item.path === location.pathname);
  }, [displayNavItems, effectivePortalRole, location.pathname, normalizedPathname]);

  if (isLoading) {
    return <LoadingSkeleton />;
  }

  if (!user) {
    // This should not happen as AppShell is only rendered for authenticated users
    return null;
  }

  // Mobile layout for patients: floating bottom nav + compact header
  const isPatient = user.role === 'patient';
  if (isPatient && isMobile) {
    const isMessagesRoute = normalizedPathname === '/messages' || normalizedPathname.startsWith('/messages/');
    const isChatWindowOpen = normalizedPathname.startsWith('/messages/');
    const showBottomNav = !isChatWindowOpen;
    const PATIENT_NAV_KEYS = ['dashboard', 'quotes', 'travel', 'payments', 'messages'] as const;

    return (
      <ErrorBoundary>
        <div className="flex min-h-screen webview-safe-screen flex-col bg-background">
          {controlModeTopBar}
          <PatientMobileHeader key={`patient-mobile-header-${locale}`} user={user} />
          <main
            className={
              isChatWindowOpen
                ? 'flex-1 min-h-0 overflow-hidden'
                : isMessagesRoute
                  ? 'flex-1 min-h-0 overflow-hidden pb-24'
                  : 'flex-1 min-h-0 pb-24 p-3'
            }
          >
            <div className="mb-3 space-y-3">{shellBanner}</div>
            <Suspense fallback={<LoadingSkeleton />}>
              {children || <Outlet />}
            </Suspense>
          </main>
          {doctorApprovalDialog}
          {showBottomNav && (
            <MobileBottomNav
              key={`patient-mobile-nav-${locale}`}
              navItems={displayNavItems}
              navKeys={PATIENT_NAV_KEYS}
              role={user.role}
              unreadCount={unreadCounts.unreadMessages}
              disableNavPrefetch={shouldThrottleControlModeNetwork}
            />
          )}
        </div>
      </ErrorBoundary>
    );
  }

  // Mobile layout for doctors: floating bottom nav + compact header
  const isDoctor = effectivePortalRole === 'doctor';
  if (isDoctor && isMobile) {
    const isMessagesRoute = normalizedPathname === '/messages' || normalizedPathname.startsWith('/messages/');
    const isChatWindowOpen = normalizedPathname.startsWith('/messages/');
    const showBottomNav = !isChatWindowOpen;
    const DOCTOR_NAV_KEYS = ['dashboard', 'patients', 'quotes', 'payments', 'messages'] as const;
    const DOCTOR_SHORT_LABELS = {
      dashboard: i18n._(
        t({
          id: 'shared.layout.AppShell.my-day',
          message: 'My Day',
        }),
      ),
    };

    return (
      <ErrorBoundary>
        <div className="flex min-h-screen webview-safe-screen flex-col bg-background">
          {controlModeTopBar}
          <DoctorMobileHeader key={`doctor-mobile-header-${locale}`} user={user} />
          <main
            className={
              isChatWindowOpen
                ? 'flex-1 min-h-0 overflow-hidden'
                : isMessagesRoute
                  ? 'flex-1 min-h-0 overflow-hidden pb-24'
                  : 'flex-1 min-h-0 pb-24 p-3'
            }
          >
            <div className="mb-3 space-y-3">{shellBanner}</div>
            <Suspense fallback={<LoadingSkeleton />}>
              {children || <Outlet />}
            </Suspense>
          </main>
          {doctorApprovalDialog}
          {showBottomNav && (
            <MobileBottomNav
              key={`doctor-mobile-nav-${locale}`}
              navItems={displayNavItems}
              navKeys={DOCTOR_NAV_KEYS}
              role={effectivePortalRole}
              shortLabels={DOCTOR_SHORT_LABELS}
              disableNavPrefetch={shouldThrottleControlModeNetwork}
              unreadCount={
                isDelegatedDoctorWorkspace
                  ? delegatedUnreadCountsQuery.data?.unreadMessages ?? 0
                  : unreadCounts.unreadMessages
              }
            />
          )}
        </div>
      </ErrorBoundary>
    );
  }

  // Default desktop/tablet layout with sidebar
  return (
    <ErrorBoundary>
      <SidebarProvider>
        <div className="flex h-screen w-full flex-col overflow-hidden bg-background">
          {controlModeTopBar}
          <div className="flex min-h-0 flex-1 overflow-hidden">
            <AppSidebar
              key={`sidebar-${locale}`}
              user={user}
              navItems={displayNavItems}
              roleOverride={effectivePortalRole}
              disableNavPrefetch={shouldThrottleControlModeNetwork}
            />
            <SidebarInset className="flex flex-col flex-1 min-w-0 overflow-hidden">
              <AppHeader
                key={`header-${locale}`}
                user={user}
                currentPage={currentNavItem}
              />
              <main
                className={`flex-1 min-w-0 ${normalizedPathname.startsWith('/quotes') || normalizedPathname.startsWith('/messages')
                  ? 'overflow-hidden'
                  : 'space-y-4 p-2 sm:p-4 md:p-6 overflow-y-auto'
                  }`}
              >
                <div className="space-y-3">{shellBanner}</div>
                <Suspense fallback={<LoadingSkeleton />}>
                  {children || <Outlet />}
                </Suspense>
              </main>
              {doctorApprovalDialog}
            </SidebarInset>
          </div>
        </div>
      </SidebarProvider>
    </ErrorBoundary>
  );
};
